import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emailTemplatesApi } from "@/api/emailTemplates";
import type { EmailTemplatePreviewBlockInput } from "@/types/emailTemplates";
import { emailTemplateKeys } from "./emailTemplateKeys";

/** Every catalogued email, grouped by audience. */
export function useEmailTemplates() {
  return useQuery({
    queryKey: emailTemplateKeys.list(),
    queryFn: () => emailTemplatesApi.list(),
  });
}

/** One template opened for editing. Disabled until a key is present. */
export function useEmailTemplate(key: string | undefined) {
  return useQuery({
    queryKey: emailTemplateKeys.detail(key ?? ""),
    queryFn: () => emailTemplatesApi.get(key as string),
    enabled: key != null && key !== "",
  });
}

/**
 * Save ONE block's draft. Nothing goes live.
 *
 * Invalidates the whole surface by prefix: a draft changes this template's
 * detail AND the list's "n drafted" column, and the two must never disagree
 * about whether an edit is pending.
 */
export function useSaveEmailTemplateDraft(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { blockKey: string; text: string; textVariant?: boolean }) =>
      emailTemplatesApi.saveDraft(key, vars.blockKey, {
        text: vars.text,
        textVariant: vars.textVariant ?? false,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

/** Publish every pending draft on the template. Changes what sends. */
export function usePublishEmailTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { note?: string | null }) =>
      emailTemplatesApi.publish(key, { note: vars.note ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

/** Roll the live copy back to a prior version (appended as a new version). */
export function useRestoreEmailTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionNumber: number) => emailTemplatesApi.restore(key, versionNumber),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

/** Clear every override so the code default renders again. */
export function useResetEmailTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => emailTemplatesApi.reset(key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

/**
 * Render a preview of the editor's current (unsaved) text.
 *
 * A POST because the unsaved blocks travel in the body, but it is a READ — it
 * persists nothing, so there is no cache to freshen.
 */
export function usePreviewEmailTemplate(key: string) {
  return useMutation({
    // no-invalidation: the preview writes nothing; it renders the supplied text
    // through the real branded renderer and returns it. Nothing cached changes.
    mutationFn: (blocks: EmailTemplatePreviewBlockInput[]) =>
      emailTemplatesApi.preview(key, blocks),
  });
}
